"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Button from "../../../components/Button";
import FormInput from "../../../components/FormInput";
import Dropdown from "../../../components/Dropdown";
import ImageDropzone from "../../../components/ImageDropzone";
import styles from "../css/creditEmployment.module.css";

const CreditEmployment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const creditFlow = location?.state?.creditFlow;
  const isReviewMode = creditFlow?.mode === "review";
  const savedData = location?.state?.formData || {};
  const creditStatus = String(creditFlow?.status || "").toLowerCase();
  const isReadOnly = isReviewMode && ["awaiting", "pending", "decision"].includes(creditStatus);

  const steps = useMemo(
    () => [
      { key: "personal", label: "Personal" },
      { key: "contact", label: "Contact" },
      { key: "employment", label: "Employment" },
      { key: "financial", label: "Financial" },
      { key: "consent", label: "Consent" },
    ],
    []
  );

  const employmentStatusOptions = useMemo(
    () => ["Employed", "Self-employed", "Student", "Unemployed"],
    []
  );

  const payFrequencyOptions = useMemo(() => ["Monthly", "Weekly"], []);

  const [form, setForm] = useState({
    employmentStatus: savedData.employmentStatus || "",
    employerName: savedData.employerName || "",
    monthlyIncome: savedData.monthlyIncome || "",
    payFrequency: savedData.payFrequency || "",
    payslip: savedData.payslip || null,
    bankStatement: savedData.bankStatement || null,
  });

  const onChange = (e) => {
    if (isReadOnly) return;
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setForm((prev) => ({ ...prev, [name]: files?.[0] ?? null }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className={styles.page}>
      <Navbar backTo="/" hideBack={isReviewMode} />

      <main className={styles.main}>
        <div className={styles.titleWrap}>
          <h1 className={styles.title}>Credit Application</h1>
          <p className={styles.subtitle}>
            Complete the form below to apply for purchase-on-credit at your pharmacy.
          </p>
        </div>

        <div className={styles.stepper} aria-label="Application steps">
          {steps.map((s, idx) => {
            const isActive = idx === 2;
            const isDone = idx < 2;
            return (
              <div key={s.key} className={styles.stepItem}>
                <div
                  className={`${styles.stepCircle} ${isActive ? styles.stepActive : ""} ${
                    isDone ? styles.stepDone : ""
                  }`}
                >
                  {isDone ? "✓" : idx + 1}
                </div>
                <div className={styles.stepLabel}>{s.label}</div>
                {idx < steps.length - 1 ? <div className={styles.stepLine} aria-hidden /> : null}
              </div>
            );
          })}
        </div>

        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon} aria-hidden>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 7h18v13H3V7z" stroke="currentColor" strokeWidth="2" />
                <path d="M7 7V4h10v3" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <div className={styles.sectionTitle}>Employment &amp; Income</div>
          </div>

          <div className={styles.grid}>
            <Dropdown
              label="Employment Status"
              name="employmentStatus"
              options={employmentStatusOptions}
              value={form.employmentStatus}
              onChange={(value) => {
                if (isReadOnly) return;
                setForm((prev) => ({ ...prev, employmentStatus: value }));
              }}
              placeholder="Select status"
              disabled={isReadOnly}
            />

            <FormInput
              label="Employer Name"
              name="employerName"
              value={form.employerName}
              onChange={onChange}
              placeholder="Employer"
              disabled={isReadOnly}
            />

            <FormInput
              label="Monthly Income"
              name="monthlyIncome"
              type="number"
              value={form.monthlyIncome}
              onChange={onChange}
              placeholder="0.00"
              prefix="R"
              min={0}
              disabled={isReadOnly}
            />

            <Dropdown
              label="Pay Frequency"
              name="payFrequency"
              options={payFrequencyOptions}
              value={form.payFrequency}
              onChange={(value) => {
                if (isReadOnly) return;
                setForm((prev) => ({ ...prev, payFrequency: value }));
              }}
              placeholder="Select frequency"
              disabled={isReadOnly}
            />

            <div className={styles.dropzoneField}>
              <label className={styles.fieldLabel}>Upload your payslip</label>
              <ImageDropzone
                id="payslip"
                name="payslip"
                accept="image/png,image/jpeg"
                value={form.payslip}
                viewUrl={savedData.payslipUrl || ""}
                onChange={onChange}
                placeholder="Drop files here"
                helper="Supported format: PNG, JPG"
                disabled={isReadOnly}
              />
            </div>

            <div className={styles.dropzoneField}>
              <label className={styles.fieldLabel}>Upload a three month bank statement</label>
              <ImageDropzone
                id="bankStatement"
                name="bankStatement"
                accept="image/png,image/jpeg"
                value={form.bankStatement}
                viewUrl={savedData.bankStatementUrl || ""}
                onChange={onChange}
                placeholder="Drop files here"
                helper="Supported format: PNG, JPG"
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.actions}>
            <Button
              variant="primary"
              type="button"
              onClick={() =>
                isReviewMode
                  ? navigate("/credits/contact", {
                      state: {
                        creditFlow,
                        formData: {
                          ...savedData,
                          ...form,
                        },
                      },
                    })
                  : navigate(-1)
              }
            >
              Back
            </Button>
            <Button
              variant="approve"
              type="button"
              onClick={() =>
                navigate("/credits/financial", {
                  state: {
                    creditFlow,
                    formData: {
                      ...savedData,
                      ...form,
                    },
                  },
                })
              }
            >
              Next
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CreditEmployment;
